<template>
  <div class="container-fluid p-0 h-100">
    <!-- Alert Messages -->
    <div
      v-if="alert.status === 'success'"
      class="alert alert-success alert-dismissible fade show"
      role="alert"
    >
      {{ alert.message }}
      <button
        type="button"
        class="close"
        @click="alert.status = ''"
        aria-label="Close"
      >
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
    <div
      v-if="alert.status === 'error'"
      class="alert alert-danger alert-dismissible fade show"
      role="alert"
    >
      {{ alert.message }}
      <button
        type="button"
        class="close"
        @click="alert.status = ''"
        aria-label="Close"
      >
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
    <div
      v-if="alert.status === 'info'"
      class="alert alert-info alert-dismissible fade show"
      role="alert"
    >
      {{ alert.message }}
      <button
        type="button"
        class="close"
        @click="alert.status = ''"
        aria-label="Close"
      >
        <span aria-hidden="true">&times;</span>
      </button>
    </div>

    <div class="d-flex justify-content-between align-items-center mb-3">
      <h3>Order Pull Schedules Calendar</h3>
      <div>
        <button class="btn btn-secondary mr-2" @click="refreshData">
          <i class="fas fa-sync-alt"></i> Refresh
        </button>
        <button class="btn btn-primary" @click="showNewEventForm">
          Add New Schedule
        </button>
      </div>
    </div>

    <div class="calendar-container">
      <!-- Loading overlay -->
      <div v-if="loading" class="loading-overlay">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>

      <!-- Calendar Header -->
      <div class="calendar-header">
        <div class="date-navigation">
          <button class="btn btn-outline-secondary" @click="previousDay">
            <i class="fas fa-chevron-left"></i>
          </button>
          <div class="date-picker-container">
            <input
              type="date"
              v-model="selectedDate"
              class="form-control date-picker"
              @change="onDateChange"
            />
            <h4 class="date-display mb-0">{{ formatDate(selectedDate) }}</h4>
          </div>
          <button class="btn btn-outline-secondary" @click="nextDay">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
        <div class="header-filters">
          <div class="filter-group">
            <label for="reportTypeFilter" class="filter-label"
              >Report Type:</label
            >
            <select
              id="reportTypeFilter"
              v-model="selectedReportType"
              class="form-control report-type-selector"
              @change="onReportTypeChange"
            >
              <option value="">All Types</option>
              <option value="REGULAR_REPORT">Regular Reports</option>
              <option value="ERROR_CHECK_REPORT">Error Check Reports</option>
              <option value="ORDER_REPORT_SAME_DAY">Same Day Reports</option>
              <option value="ORDER_REPORT_NEXT_DAY">Next Day Reports</option>
              <option value="ORDER_REPORT_V_DAY">Specific Day Reports</option>
              <option value="LAKEWOOD_REPORT">Lakewood Reports</option>
              <option value="SHR_REPORT">SHR Reports</option>
              <option value="ORDER_REPORT_MAXIMUM">Maximum Reports</option>
              <option value="NOT_PULLED_REPORT">Not Pulled Reports</option>
              <option value="NOT_SENT_REPORT">Not Sent Reports</option>
              <option value="ERROR_REPORT">Error Reports</option>
            </select>
          </div>
          <div class="filter-group">
            <label for="regionFilter" class="filter-label"
              >Region:</label
            >
            <select
              id="regionFilter"
              v-model="selectedRegion"
              class="form-control report-type-selector"
              @change="onRegionChange"
            >
              <option value="">All Regions</option>
              <option
                v-for="(optType, index) in regionsOpt"
                :key="index"
                :value="optType.value"
              >
                {{ optType.text }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <!-- Calendar Body -->
      <div class="calendar-body">
        <!-- Time Column -->
        <div class="time-column">
          <div class="time-header">Time</div>
          <div
            v-for="hour in timeSlots"
            :key="hour.time"
            class="time-slot"
            :style="{ height: '120px' }"
          >
            <span class="time-label">{{ hour.display }}</span>
          </div>
        </div>

        <!-- Events Column -->
        <div class="events-column">
          <div class="events-header">Events</div>
          <div
            class="events-container"
            :style="{ height: timeSlots.length * 120 + 'px', width: eventsContainerWidth }"
          >
            <!-- Time Grid Lines -->
            <div
              v-for="(hour, index) in timeSlots"
              :key="'grid-' + hour.time"
              class="time-grid-line"
              :style="{ top: index * 120 + 'px' }"
            ></div>

            <!-- Current Time Line -->
            <div
              v-if="showCurrentTimeLine"
              class="current-time-line"
              :style="{ top: currentTimePosition + 'px' }"
            >
              <div class="current-time-indicator"></div>
            </div>

            <!-- Events -->
            <div
              v-for="event in eventsForSelectedDate"
              :key="event.id"
              class="event-item"

              :class="[
                'event-type-' + event.reportType,
                event.status === 'inactive' ? 'event-inactive' : '',
                event.status === 'disabled' ? 'event-disabled' : ''
              ]"
              :style="getEventStyle(event)"
              @click="selectEvent(event)"
            >
              <div class="event-content">
                <div class="event-title">{{ event.reportName }}</div>
                <div class="event-type">
                  {{ formatReportType(event.reportType) }}
                  <span class="event-status" v-if="getEventStatus(event)">
                    - {{ getEventStatus(event) }}
                  </span>
                </div>
                <div class="event-time">{{ formatEventTime(event) }}</div>
                <button
                  class="event-menu-btn"
                  @click.stop="showMenu($event, event)"
                >
                  ⋮
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Floating event action menu -->
      <div
        class="event-menu"
        v-if="openMenuId !== null"
        :style="{
          top: menuPosition.top + 'px',
          left: menuPosition.left + 'px'
        }"
      >
        <div @click="editEvent(currentEvent)">Edit</div>
        <div @click="editThisDayEvent(currentEvent)" v-if="!isEventPast(currentEvent)">Edit for this day</div>
        <div @click="manuallyGenReport(currentEvent)">Manually Run</div>
        <div
          @click="enableEvent(currentEvent)"
          v-if="
            !isEventPast(currentEvent) &&
            currentEvent &&
              getScheduleState(currentEvent.scheduleId) === 'DISABLED'
          "
        >
          Enable
        </div>
        <div
          @click="disableEvent(currentEvent)"
          v-if="
            !isEventPast(currentEvent) &&
            currentEvent &&
              getScheduleState(currentEvent.scheduleId) === 'ENABLED'
          "
        >
          Disable all days
        </div>
        <div
          @click="disableThisDayEvent(currentEvent)"
          v-if="
            !isEventPast(currentEvent) &&
            currentEvent &&
              getScheduleState(currentEvent.scheduleId) === 'ENABLED' &&
              currentEvent.isDisabledThisDay === false
          "
        >
          Disable this day
        </div>
        <div
          @click="enableThisDayEvent(currentEvent)"
          v-if="!isEventPast(currentEvent) && currentEvent && currentEvent.isDisabledThisDay === true"
        >
          Enable this day
        </div>
        <!-- <div @click="deleteEvent(currentEvent)" class="text-danger">Delete</div> -->
      </div>

      <!-- Schedule Form Modal (new / edit / edit-this-day) -->
      <NewScheduleForm
        v-if="activeForm"
        :regionsOpt="regionsOpt"
        :sortHeaderNameOpt="sortHeaderNameOpt"
        :additionalReportTypesOpt="additionalReportTypesOpt"
        :additionalFileTypesOpt="additionalFileTypesOpt"
        :isOverride="activeForm === 'editThisDay'"
        :initialData="activeFormData"
        @save="onFormSaved"
        @cancel="closeForm"
        @error="displayMessage('error', $event)"
        @success="displayMessage('success', $event)"
      />
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

import NewScheduleForm from "@/components/forms/NewScheduleForm.vue";

export default {
  name: "PullSchedules",
  components: {
    NewScheduleForm
  },
  data() {
    const today = dayjs().format("YYYY-MM-DD");
    return {
      selectedDate: today,
      selectedReportType: "",
      selectedRegion: "",
      openMenuId: null,
      currentEvent: null,
      menuPosition: { top: 0, left: 0 },
      loading: false,
      events: [],
      schedules: [],
      reports: [],
      reportsMap: {},
      eventColumnCache: {}, // Cache for event column assignments
      activeForm: null, // 'new' | 'edit' | 'editThisDay'
      activeFormData: null,
      alert: {
        status: "",
        message: ""
      },
      regionsOpt: [],
      additionalReportTypesOpt: [
        { text: "Select an option", value: "" },
        { text: "Gopher Report", value: "ADDITIONAL_GOPHER_IT_REPORT" },
        { text: "Flashbox Report", value: "ADDITIONAL_FLASHBOX_REPORT" },
        { text: "Midland Report", value: "ADDITIONAL_MIDLAND_REPORT" },
        {
          text: "Go Speed Logistics Report",
          value: "ADDITIONAL_GO_SPEED_LOGISTICS_REPORT"
        }
      ],
      additionalFileTypesOpt: [
        { text: "Select an option", value: "" },
        { text: "Excel", value: "excel" },
        { text: "CSV", value: "csv" }
      ],
      consolidateOrderOpt: [
        { text: "False", value: false },
        { text: "True", value: true }
      ],
      sortHeaderNameOpt: [
        { text: "Bouquet", value: "Bouquet" },
        { text: "Recipient_Name", value: "Recipient_Name" }
      ]
    };
  },
  computed: {
    timeSlots() {
      const slots = [];
      for (let hour = 4; hour <= 23; hour++) {
        slots.push({
          time: hour,
          display: `${hour.toString().padStart(2, "0")}:00`
        });
      }
      return slots;
    },
    eventsForSelectedDate() {
      return this.events.filter(event => {
        // Extract date part from event start time and compare directly
        const eventDate = event.start.slice(0, 10);
        const dateMatches = eventDate === this.selectedDate;

        // Filter by report type if selected
        let typeMatches = false;

        if (!this.selectedReportType) {
          // Show all types when no filter is selected
          typeMatches = true;
        } else if (this.selectedReportType === "ERROR_CHECK_REPORT") {
          // Show NOT_PULLED_REPORT and NOT_SENT_REPORT
          typeMatches =
            event.reportType === "NOT_PULLED_REPORT" ||
            event.reportType === "NOT_SENT_REPORT" ||
            event.reportType === "ERROR_REPORT";
        } else if (this.selectedReportType === "REGULAR_REPORT") {
          // Show all reports except NOT_PULLED_REPORT and NOT_SENT_REPORT
          typeMatches =
            event.reportType !== "NOT_PULLED_REPORT" &&
            event.reportType !== "NOT_SENT_REPORT" &&
            event.reportType !== "ERROR_REPORT";
        } else if (this.selectedReportType === "LAKEWOOD_REPORT") {
          typeMatches =
            event.reportType === "LAKEWOOD_SAME_DAY_REPORT" ||
            event.reportType === "LAKEWOOD_NEXT_DAY_REPORT";
        } else if (this.selectedReportType === "SHR_REPORT") {
          typeMatches =
            event.reportType === "SHR_SAME_DAY_REPORT" ||
            event.reportType === "SHR_NEXT_DAY_REPORT";
        } else {
          // Show exact match for specific report type
          typeMatches = event.reportType === this.selectedReportType;
        }

        let regionMatches = false;
        if (!this.selectedRegion) {
          // Show all regions when no filter is selected
          regionMatches = true;
        } else if (event.deliveryRegion.includes(this.selectedRegion)) {
          regionMatches = true;
        }

        return dateMatches && typeMatches && regionMatches;
      });
    },
    showCurrentTimeLine() {
      // Only show current time line if viewing today's date
      const today = dayjs().format("YYYY-MM-DD");
      return this.selectedDate === today;
    },
    currentTimePosition() {
      // Get current time in Winnipeg timezone (America/Winnipeg)
      const now = dayjs().tz('America/Winnipeg');
      const currentHour = now.hour();
      const currentMinutes = now.minute();

      // Calculate position based on time (starting from 4 AM)
      const currentTime = currentHour + currentMinutes / 60;
      return (currentTime - 4) * 120;
    },
    eventsContainerWidth() {
      const eventWidth = 300;
      const gap = 10;
      const minColumns = 3;
      const maxColumn = Object.values(this.eventColumnCache).reduce((max, col) => Math.max(max, col), 0);
      const columns = Math.max(minColumns, maxColumn + 1);
      return `${columns * (eventWidth + gap)}px`;
    },
  },
  watch: {
    selectedDate() {
      this.generateEventsForSelectedDate();
    }
  },
  async created() {
    this.fetchEvents();

    // Fetch regions data like in OrderReports.vue
    try {
      const response = await this.axios.get(
        process.env.VUE_APP_API_ADMIN_API_ENDPOINT + "/order-pull/regions",
        {
          headers: {
            Authorization: "Bearer " + this.$store.getters.idToken
          }
        }
      );

      if (response.data && response.data.records) {
        this.regionsOpt = [];
        for (const data of response.data.records) {
          const region = {
            text: data.name + "(" + data.province_code + ")",
            value: data.tag,
            provinceCode: data.province_code
          };
          this.regionsOpt.push(region);
        }
        this.regionsOpt.sort((a, b) => (a.text > b.text ? 1 : -1));
      }
    } catch (error) {
      console.error("Error fetching regions:", error);
      // Keep the default regions if API fails
    }
  },
  mounted() {
    document.addEventListener("click", this.resetMenu);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.resetMenu);
  },
  methods: {
    async fetchEvents() {
      try {
        this.loading = true;

        // Fetch schedules and reports data
        const [
          schedulesResponse,
          reportsResponse,
          overrideResponse
        ] = await Promise.all([
          this.axios.get(
            `${process.env.VUE_APP_API_ADMIN_API_ENDPOINT}/order-pull/schedules`
          ),
          this.axios.get(
            `${process.env.VUE_APP_API_ADMIN_API_ENDPOINT}/order-pull/config`
          ),
          this.axios.get(
            `${process.env.VUE_APP_API_ADMIN_API_ENDPOINT}/order-pull/override`
          )
        ]);

        this.schedules = schedulesResponse?.data || [];
        this.reports = reportsResponse?.data || [];
        this.closed = overrideResponse?.data?.closed || [];
        this.override = overrideResponse?.data?.override || [];

        this.reports.forEach(report => {
          console.log("reportType:", report.reportType, report.deliveryDate, report.noOfDaysToDeliveryDate);
          if (report.reportType === "LAKEWOOD_REPORT") {
            if ((!report.deliveryDate || report.deliveryDate === "Invalid Date")
                && report.noOfDaysToDeliveryDate === 1) {
              report.reportType = "LAKEWOOD_NEXT_DAY_REPORT";
            } else if ((!report.deliveryDate || report.deliveryDate === "Invalid Date")
                && report.noOfDaysToDeliveryDate === 0) {
              report.reportType = "LAKEWOOD_SAME_DAY_REPORT";
            }
          } else if (report.reportType === "SHR_REPORT") {
            if ((!report.deliveryDate || report.deliveryDate === "Invalid Date")
                && report.noOfDaysToDeliveryDate === 1) {
              report.reportType = "SHR_NEXT_DAY_REPORT";
            } else if ((!report.deliveryDate || report.deliveryDate === "Invalid Date")
                && report.noOfDaysToDeliveryDate === 0) {
              report.reportType = "SHR_SAME_DAY_REPORT";
            }
          } else if (report.reportType === "ORDER_REPORT_V_DAY"
              && (!report.deliveryDate || report.deliveryDate === "Invalid Date")
              && report.noOfDaysToDeliveryDate === 2) {
            report.reportType = "ORDER_REPORT_NEXT_DAY_PLUS_1";
          }
          this.reportsMap[report.id] = report;
        });

        // Generate events for the selected date
        this.generateEventsForSelectedDate();
      } catch (error) {
        console.error("Error fetching events:", error);
        // Fallback to empty events array
        this.events = [];
      } finally {
        this.loading = false;
      }
    },

    async refreshData() {
      await this.fetchEvents();
      this.displayMessage("success", "Data refreshed successfully");
    },

    convertScheduleToEvents(schedule, report) {
      // console.log("schedule:", schedule);
      // console.log("report:", report);
      // console.log("scheduleExpression:", schedule.scheduleExpression);
      const events = [];

      // Parse cron expression (AWS EventBridge format)
      // Format: "cron(minutes hours day-of-month month day-of-week year)"
      let cronExpression = schedule.scheduleExpression;
      if (cronExpression.startsWith("cron(") && cronExpression.endsWith(")")) {
        cronExpression = cronExpression.slice(5, -1); // Remove "cron(" and ")"
      }

      const cronParts = cronExpression.split(" ");
      if (cronParts.length !== 6) {
        console.warn("Invalid cron expression:", schedule.scheduleExpression);
        return events;
      }

      const [minutes, hours, dayOfMonth, month, dayOfWeek, year] = cronParts;
      // console.log("dayOfWeek:", dayOfWeek);

      // Parse the selected date using dayjs
      const selectedDate = dayjs(this.selectedDate);
      const selectedDateForClosed = selectedDate.format("MM/DD/YYYY");
      // console.log("this.selectedDate:", this.selectedDate);
      // console.log("selected date:", selectedDate.format());
      const selectedYear = selectedDate.year();
      const selectedMonth = selectedDate.month() + 1; // dayjs months are 0-based, convert to 1-based
      const selectedDay = selectedDate.date();
      const selectedDayOfWeek = selectedDate.day(); // 0 = Sunday, 1 = Monday, etc.
      // console.log("selectedYear:", selectedYear);
      // console.log("selectedMonth:", selectedMonth);
      // console.log("selectedDay:", selectedDay);
      // console.log("selectedDayOfWeek:", selectedDayOfWeek);

      // Check if schedule matches the selected date
      if (
        !this.cronMatches(year, selectedYear) ||
        !this.cronMatches(month, selectedMonth) ||
        !this.cronMatches(dayOfMonth, selectedDay) ||
        !this.cronMatchesDayOfWeek(dayOfWeek, selectedDayOfWeek)
      ) {
        // console.log("not matched");
        return events;
      }

      // Parse time values - handle multiple values, ranges, and wildcards
      const hourValues = this.parseCronField(hours, 0, 23);
      const minuteValues = this.parseCronField(minutes, 0, 59);
      // console.log("hourValues:", hourValues);
      // console.log("minuteValues:", minuteValues);

      const closedItem = this.closed.find(item => item.id === report.id);
      let closedThisDay = false;
      if (closedItem?.closed_dates) {
        closedThisDay = closedItem.closed_dates.find(
          item => item === selectedDateForClosed
        )
          ? true
          : false;
      }

      const overrideItem = this.override.find(item => item.id === report.id);
      let overrideThisDay = false;
      if (overrideItem?.closed_dates) {
        overrideThisDay = overrideItem[selectedDateForClosed] ? true : false;
      }

      // Generate events for each time combination
      hourValues.forEach(eventHour => {
        minuteValues.forEach(eventMinute => {
          const startTime = `${eventHour
            .toString()
            .padStart(2, "0")}:${eventMinute.toString().padStart(2, "0")}`;

          // Calculate end time (30-minute duration)
          let finalEndHour = eventHour;
          let finalEndMinute = eventMinute + 30;
          if (finalEndMinute >= 60) {
            finalEndHour += 1;
            finalEndMinute -= 60;
          }

          const endTime = `${finalEndHour
            .toString()
            .padStart(2, "0")}:${finalEndMinute.toString().padStart(2, "0")}`;

          // Determine status: disabled if schedule is DISABLED OR if closed this day
          let eventStatus = "active";
          if (schedule.state === "DISABLED") {
            eventStatus = "disabled";
          } else if (closedThisDay) {
            eventStatus = "inactive";
          }

          events.push({
            id: `${schedule.name}_${report.id}_${eventHour}_${eventMinute}`,
            // schedule fields
            scheduleId: schedule.name,
            cronText: schedule.scheduleExpression,
            scheduleState: schedule.state,
            scheduleDescription: schedule.description ?? "",
            // report fields
            reportId: report.id,
            reportName: report.reportName ?? schedule.name,
            reportType: report.reportType ?? "ORDER_REPORT_SAME_DAY",
            deliveryRegion: report.deliveryRegion ?? [],
            deliveryDate: report.deliveryDate ?? "",
            description: report.description ?? "",
            defaultDeliveryStartTime: report.defaultDeliveryStartTime ?? "",
            skippedTag: report.skippedTag ?? "",
            deliveryWindowExcluded: report.deliveryWindowExcluded ?? false,
            deliveryWindow: report.deliveryWindow ?? [],
            consolidateOrder: report.consolidateOrder ?? false,
            sortHeaderName: report.sortHeaderName ?? "Bouquet",
            additionalReportType: report.additionalReportType ?? "",
            additionalReportName: report.additionalReportName ?? "",
            additionalFileType: report.additionalFileType ?? "",
            excludingRegion: report.excludingRegion ?? [],
            provinceCode: report.provinceCode ?? "",
            fileType: report.fileType ?? "excel",
            noOfDaysToDeliveryDate: report.noOfDaysToDeliveryDate ?? 0,
            // computed event fields
            start: `${this.selectedDate} ${startTime}`,
            end: `${this.selectedDate} ${endTime}`,
            status: eventStatus,
            isDisabledThisDay: closedThisDay,
            isOverride: overrideThisDay
          });
        });
      });

      // console.log("evnets:", events);
      return events;
    },

    // Map month names to numbers (1-based)
    resolveMonthValue(token) {
      const monthNameMap = {
        JAN: 1, FEB: 2, MAR: 3, APR: 4, MAY: 5, JUN: 6,
        JUL: 7, AUG: 8, SEP: 9, OCT: 10, NOV: 11, DEC: 12
      };
      const upper = token.trim().toUpperCase();
      if (monthNameMap[upper] !== undefined) return monthNameMap[upper];
      const num = parseInt(token.trim());
      return isNaN(num) ? null : num;
    },

    // Helper method to check if a cron field matches a value
    cronMatches(cronField, value) {
      if (cronField === "*") return true;
      if (cronField === "?") return true;

      // Resolve the field token (handles month names like APR)
      const resolved = this.resolveMonthValue(cronField);
      if (resolved !== null && !cronField.includes(",") && !cronField.includes("-") && !cronField.includes("/")) {
        return resolved === value;
      }

      // Handle single numeric values
      if (!isNaN(cronField)) {
        return parseInt(cronField) === value;
      }

      // Handle comma-separated values (e.g., "1,3,5" or "JAN,APR,JUL")
      if (cronField.includes(",")) {
        const values = cronField.split(",").map(v => this.resolveMonthValue(v));
        return values.includes(value);
      }

      // Handle ranges (e.g., "1-5" or "JAN-MAR")
      if (cronField.includes("-")) {
        const parts = cronField.split("-");
        const start = this.resolveMonthValue(parts[0]);
        const end = this.resolveMonthValue(parts[1]);
        return value >= start && value <= end;
      }

      // Handle step values (e.g., "*/2", "1-10/2")
      if (cronField.includes("/")) {
        const [range, step] = cronField.split("/");
        const stepValue = parseInt(step);

        if (range === "*") {
          return value % stepValue === 0;
        } else if (range.includes("-")) {
          const parts = range.split("-");
          const start = this.resolveMonthValue(parts[0]);
          const end = this.resolveMonthValue(parts[1]);
          return (
            value >= start && value <= end && (value - start) % stepValue === 0
          );
        }
      }

      return false;
    },

    // Helper method to check day of week matching (handles AWS EventBridge format)
    cronMatchesDayOfWeek(cronField, jsDay) {
      if (cronField === "*" || cronField === "?") return true;

      // Convert day names to numbers
      const dayNameMap = {
        SUN: 0,
        MON: 1,
        TUE: 2,
        WED: 3,
        THU: 4,
        FRI: 5,
        SAT: 6,
        Sun: 0,
        Mon: 1,
        Tue: 2,
        Wed: 3,
        Thu: 4,
        Fri: 5,
        Sat: 6
      };

      // Handle day name ranges like "Sun-Sat"
      if (
        cronField.includes("-") &&
        (isNaN(cronField.split("-")[0]) || isNaN(cronField.split("-")[1]))
      ) {
        const [startDay, endDay] = cronField.split("-").map(d => d.trim());
        // console.log("startDay:", startDay, ", endDay:", endDay);

        // Convert day names to numbers
        let startNum = dayNameMap[startDay.toUpperCase()];
        let endNum = dayNameMap[endDay.toUpperCase()];
        // console.log("startNum:", startNum, ", endNum:", endNum);

        // If we couldn't convert, try as numbers
        if (startNum === undefined) startNum = parseInt(startDay);
        if (endNum === undefined) endNum = parseInt(endDay);

        // Handle AWS EventBridge format where 1=Sunday
        if (startNum === undefined && !isNaN(startDay)) {
          startNum = parseInt(startDay);
          if (startNum === 1) startNum = 0;
          // Convert 1=Sunday to 0=Sunday
          else if (startNum >= 2 && startNum <= 7) startNum = startNum - 1; // Convert 2-7 to 1-6
        }

        if (endNum === undefined && !isNaN(endDay)) {
          endNum = parseInt(endDay);
          if (endNum === 1) endNum = 0;
          // Convert 1=Sunday to 0=Sunday
          else if (endNum >= 2 && endNum <= 7) endNum = endNum - 1; // Convert 2-7 to 1-6
        }

        // Special case: Sun-Sat means every day
        if (
          (startNum === 0 && endNum === 6) ||
          (startDay.toUpperCase() === "SUN" && endDay.toUpperCase() === "SAT")
        ) {
          return true;
        }

        // Handle wrap-around ranges (e.g., Fri-Mon)
        if (startNum > endNum) {
          return jsDay >= startNum || jsDay <= endNum;
        } else {
          return jsDay >= startNum && jsDay <= endNum;
        }
      }

      // Handle single day names
      if (dayNameMap[cronField.toUpperCase()] !== undefined) {
        return jsDay === dayNameMap[cronField.toUpperCase()];
      }

      // Convert JS day (0=Sunday) to cron day format
      // AWS EventBridge: 1=Sunday, 2=Monday, ..., 7=Saturday
      // But also supports: 0=Sunday, 1=Monday, ..., 6=Saturday
      const awsDay = jsDay === 0 ? 1 : jsDay + 1; // Convert to 1=Sunday format
      // console.log("jsDay:", jsDay);
      // console.log("awsDay:", awsDay);
      const standardDay = jsDay; // Keep 0=Sunday format

      // Handle single numeric values
      if (!isNaN(cronField)) {
        const cronDay = parseInt(cronField);
        return (
          cronDay === awsDay ||
          cronDay === standardDay ||
          (cronDay === 7 && jsDay === 0) ||
          (cronDay === 0 && jsDay === 0)
        );
      }

      // Handle comma-separated values (could be numbers or day names)
      if (cronField.includes(",")) {
        const values = cronField.split(",").map(v => v.trim());
        return values.some(value => {
          // Check if it's a day name
          if (dayNameMap[value.toUpperCase()] !== undefined) {
            return jsDay === dayNameMap[value.toUpperCase()];
          }
          // Check if it's a number
          if (!isNaN(value)) {
            const cronDay = parseInt(value);
            return (
              cronDay === awsDay ||
              cronDay === standardDay ||
              (cronDay === 7 && jsDay === 0) ||
              (cronDay === 0 && jsDay === 0)
            );
          }
          return false;
        });
      }

      // Handle numeric ranges
      if (
        cronField.includes("-") &&
        !isNaN(cronField.split("-")[0]) &&
        !isNaN(cronField.split("-")[1])
      ) {
        const [start, end] = cronField.split("-").map(v => parseInt(v.trim()));
        // console.log("start:", start, ", end:", end);
        return (
          (awsDay >= start && awsDay <= end) ||
          (standardDay >= start && standardDay <= end)
        );
      }

      return false;
    },

    // Helper method to parse cron field and return array of values
    parseCronField(cronField, min, max) {
      if (cronField === "*") {
        // For time fields, return reasonable defaults instead of all values
        if (max === 23) return [12]; // Default hour: noon
        if (max === 59) return [0]; // Default minute: :00
        return Array.from({ length: max - min + 1 }, (_, i) => min + i);
      }

      // Try resolving as a month name or plain number
      const resolved = this.resolveMonthValue(cronField);
      if (resolved !== null && !cronField.includes(",") && !cronField.includes("-") && !cronField.includes("/")) {
        return [resolved];
      }

      if (!isNaN(cronField)) {
        return [parseInt(cronField)];
      }

      if (cronField.includes(",")) {
        return cronField
          .split(",")
          .map(v => this.resolveMonthValue(v))
          .filter(v => v !== null && v >= min && v <= max);
      }

      if (cronField.includes("-") && !cronField.includes("/")) {
        const parts = cronField.split("-");
        const start = this.resolveMonthValue(parts[0]);
        const end = this.resolveMonthValue(parts[1]);
        return Array.from(
          { length: end - start + 1 },
          (_, i) => start + i
        ).filter(v => v >= min && v <= max);
      }

      if (cronField.includes("/")) {
        const [range, step] = cronField.split("/");
        const stepValue = parseInt(step);
        const values = [];

        if (range === "*") {
          for (let i = min; i <= max; i += stepValue) {
            values.push(i);
          }
        } else if (range.includes("-")) {
          const [start, end] = range.split("-").map(v => parseInt(v.trim()));
          for (let i = start; i <= end; i += stepValue) {
            values.push(i);
          }
        }

        return values.filter(v => v >= min && v <= max);
      }

      return [min]; // Fallback
    },

    generateEventsForSelectedDate() {
      // Clear the column cache when regenerating events
      this.eventColumnCache = {};

      // Convert schedules to calendar events for the selected date
      this.events = [];
      this.schedules.forEach(schedule => {
        if (
          schedule.input &&
          schedule.input.uuid &&
          this.reportsMap[schedule.input.uuid]
        ) {
          const report = this.reportsMap[schedule.input.uuid];
          const events = this.convertScheduleToEvents(schedule, report);
          this.events.push(...events);
        } else {
          console.warn("not in report config: ", schedule);
        }
      });

      // Sort events by start time
      this.events.sort((a, b) => {
        if (a.start < b.start) return -1;
        if (a.start > b.start) return 1;
        return 0;
      });

      // Pre-compute column assignments in a single O(n²) pass (no recursion)
      // columnEndTimes[col] = the end time string of the last event placed in that column
      const columnEndTimes = [];
      for (const event of this.events) {
        let placed = false;
        for (let col = 0; col < columnEndTimes.length; col++) {
          if (event.start >= columnEndTimes[col]) {
            this.eventColumnCache[event.id] = col;
            columnEndTimes[col] = event.end;
            placed = true;
            break;
          }
        }
        if (!placed) {
          const col = columnEndTimes.length;
          this.eventColumnCache[event.id] = col;
          columnEndTimes.push(event.end);
        }
      }

      console.log("total events", this.events.length);
    },

    formatDate(dateString) {
      // Use dayjs to format the date
      return dayjs(dateString).format("dddd, MMMM D, YYYY");
    },

    formatEventTime(event) {
      const start = dayjs.tz(event.start, 'America/Winnipeg');
      const end = dayjs.tz(event.end, 'America/Winnipeg');
      return `${start.format("HH:mm")} - ${end.format("HH:mm")}`;
    },

    formatReportType(reportType) {
      const typeMap = {
        ORDER_REPORT_SAME_DAY: "Same Day",
        ORDER_REPORT_NEXT_DAY: "Next Day",
        ORDER_REPORT_NEXT_DAY_PLUS_1: "Next Day +1",
        ORDER_REPORT_V_DAY: "Specific Day",
        LAKEWOOD_SAME_DAY_REPORT: "Lakewood Same Day",
        LAKEWOOD_NEXT_DAY_REPORT: "Lakewood Next Day",
        SHR_SAME_DAY_REPORT: "SHR Same Day",
        SHR_NEXT_DAY_REPORT: "SHR Same Day",
        ORDER_REPORT_MAXIMUM: "Maximum",
        NOT_PULLED_REPORT: "Not Pulled",
        NOT_SENT_REPORT: "Not Sent",
        ERROR_REPORT: "Error Report"
      };
      return typeMap[reportType] || reportType;
    },

    getEventStatus(event) {
      // Check status in priority order
      if (event.isDisabledThisDay) {
        return "Disabled This Day";
      }
      if (event.isOverride) {
        return "Overrided";
      }
      if (event.status === "disabled") {
        return "Disabled";
      }
      return null; // No status to display
    },

    getEventStyle(event) {
      // Parse HH:mm directly from the stored string (format: "YYYY-MM-DD HH:mm")
      const [, startTime] = event.start.split(" ");
      const [, endTime] = event.end.split(" ");
      const [startH, startM] = startTime.split(":").map(Number);
      const [endH, endM] = endTime.split(":").map(Number);

      const startHour = startH + startM / 60;
      const endHour = endH + endM / 60;
      const duration = endHour - startHour;

      const topPosition = (startHour - 4) * 120;
      const height = duration * 120;

      const column = this.getEventColumn(event);
      const width = 300;
      const gap = 10;
      const left = column * (width + gap);

      return {
        position: "absolute",
        top: `${topPosition}px`,
        left: `${left}px`,
        width: `${width}px`,
        height: `${Math.max(height, 40)}px`,
        zIndex: 10 + column
      };
    },

    getEventColumn(targetEvent) {
      return this.eventColumnCache[targetEvent.id] ?? 0;
    },

    getOverlappingEvents(targetEvent) {
      const targetStart = dayjs.tz(targetEvent.start, 'America/Winnipeg');
      const targetEnd = dayjs.tz(targetEvent.end, 'America/Winnipeg');

      const overlapping = this.eventsForSelectedDate
        .filter(event => {
          const eventStart = dayjs.tz(event.start, 'America/Winnipeg');
          const eventEnd = dayjs.tz(event.end, 'America/Winnipeg');

          return eventStart.isBefore(targetEnd) && eventEnd.isAfter(targetStart);
        })
        .sort((a, b) => {
          // Sort by start time
          if (a.start < b.start) return -1;
          if (a.start > b.start) return 1;
          return 0;
        })
        .map(event => event.id);

      return overlapping;
    },

    previousDay() {
      this.selectedDate = dayjs(this.selectedDate)
        .subtract(1, "day")
        .format("YYYY-MM-DD");
    },

    nextDay() {
      this.selectedDate = dayjs(this.selectedDate)
        .add(1, "day")
        .format("YYYY-MM-DD");
    },

    onDateChange() {
      // watcher on selectedDate handles regeneration
    },

    onReportTypeChange() {
      // Report type filter automatically updates eventsForSelectedDate
      // This method can be used for additional logic if needed
    },

    onRegionChange() {
      // Report region filter automatically updates eventsForSelectedDate
      // This method can be used for additional logic if needed
    },

    selectEvent(event) {
      // Handle event selection if needed
    },

    getScheduleState(scheduleId) {
      const schedule = this.schedules.find(s => s.name === scheduleId);
      return schedule ? schedule.state : "DISABLED";
    },

    isEventPast(event) {
      if (!event) return false;

      // Parse event end time in Winnipeg timezone
      const eventStart = dayjs.tz(event.start, 'America/Winnipeg');
      const now = dayjs().tz('America/Winnipeg');

      // Event is past if its end time is before now
      return eventStart.isBefore(now);
    },

    showMenu(eventDomEvent, calendarEvent) {
      const rect = eventDomEvent.currentTarget.getBoundingClientRect();
      this.menuPosition = {
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX
      };
      this.openMenuId = calendarEvent.id;
      this.currentEvent = calendarEvent;
    },

    resetMenu() {
      this.openMenuId = null;
      this.currentEvent = null;
    },

    displayMessage(status, message) {
      this.alert.status = status;
      this.alert.message = message;
    },

    editEvent(event) {
      this.activeFormData = {
        reportId: event.reportId,
        scheduleId: event.scheduleId,
        cronText: event.cronText,
        reportName: event.reportName,
        reportType: event.reportType,
        deliveryRegion: event.deliveryRegion,
        deliveryDate: event.deliveryDate,
        description: event.description,
        defaultDeliveryStartTime: event.defaultDeliveryStartTime,
        skippedTag: event.skippedTag,
        deliveryWindowExcluded: event.deliveryWindowExcluded,
        deliveryWindow: event.deliveryWindow,
        consolidateOrder: event.consolidateOrder,
        sortHeaderName: event.sortHeaderName,
        additionalReportType: event.additionalReportType,
        additionalReportName: event.additionalReportName,
        additionalFileType: event.additionalFileType,
        excludingRegion: event.excludingRegion,
        provinceCode: event.provinceCode,
        fileType: event.fileType,
        noOfDaysToDeliveryDate: event.noOfDaysToDeliveryDate
      };
      this.activeForm = "edit";
      this.resetMenu();
    },

    async onEditSaved() {
      await this.refreshData();
      this.activeForm = null;
      this.activeFormData = null;
    },

    cancelEditEvent() {
      this.activeForm = null;
      this.activeFormData = null;
    },

    editThisDayEvent(event) {
      this.activeFormData = {
        ...event,
        date: dayjs(event.start).format("YYYY-MM-DD"),
        startTime: dayjs(event.start).format("HH:mm"),
        endTime: dayjs(event.end).format("HH:mm"),
        dayStatus: event.isDisabledThisDay ? "disabled" : "enabled",
        dayNotes: ""
      };
      this.activeForm = "editThisDay";
      this.resetMenu();
    },

    async onEditThisDaySaved() {
      await this.refreshData();
      this.activeForm = null;
      this.activeFormData = null;
    },

    async manuallyGenReport(event) {
      if (confirm(`Manually generate report for ${event.reportName}?`)) {
        this.loading = true;

        // Make API call to manually trigger the report generation
        const params = {
          uuid: event.reportId,
          prefix: "(Manual)"
        };
        // console.log("event:", event);
        this.$axios
          .post(
            process.env.VUE_APP_API_ADMIN_API_ENDPOINT + `/order-pull/export`,
            params
          )
          .then(() => {
            this.displayMessage(
              "success",
              `Report ${event.reportName} has been manually triggered for generation.`
            );
          })
          .catch(() => {
            this.displayMessage("error", "Unable to generate report");
          })
          .finally(() => {
            this.loading = false;
            this.reportGenJob = null;
          });
      }
      this.resetMenu();
    },

    async disableThisDayEvent(event) {
      const eventDate = dayjs(event.start).format("MM/DD/YYYY");
      const eventTime = dayjs(event.start).format("HH:mm");
      // event.status = "disabled";

      if (
        confirm(
          `Disable this day's event for ${event.reportName} on ${eventDate} at ${eventTime}?`
        )
      ) {
        this.loading = true;
        const url = `${process.env.VUE_APP_API_ADMIN_API_ENDPOINT}/order-pull/override/disable/${event.reportId}`;
        console.log("event:", event);
        const params = {
          date: eventDate
        };
        console.log("params:", params);
        this.$axios
          .post(url, params)
          .then(() => {
            // this.refreshData();
            // Update local state
            this.updateClosedItem(event.reportId, true);
            // Regenerate events to reflect the change
            this.generateEventsForSelectedDate();

            this.displayMessage("success", "Successfully change status");
          })
          .catch((err) => {
            console.error(err);
            this.displayMessage("error", "Unable to change status");
          })
          .finally(() => {
            this.loading = false;
          });

        this.displayMessage(
          "success",
          `Event for ${event.reportName} on ${eventDate} has been disabled.`
        );
      }
      this.resetMenu();
    },

    async enableThisDayEvent(event) {
      const eventDate = dayjs(event.start).format("MM/DD/YYYY");
      const eventTime = dayjs(event.start).format("HH:mm");

      if (
        confirm(
          `Enable this day's event for ${event.reportName} on ${eventDate} at ${eventTime}?`
        )
      ) {
        this.loading = true;
        const url = `${process.env.VUE_APP_API_ADMIN_API_ENDPOINT}/order-pull/override/enable/${event.reportId}`;
        console.log("event:", event);
        const params = {
          date: eventDate
        };
        console.log("params:", params);
        this.$axios
          .post(url, params)
          .then(() => {
            // this.refreshData();
            // Update local state
            this.updateClosedItem(event.reportId, false);
            // Regenerate events to reflect the change
            this.generateEventsForSelectedDate();

            this.displayMessage("success", "Successfully change status");
          })
          .catch((err) => {
            console.error(err);
            this.displayMessage("error", "Unable to change status");
          })
          .finally(() => {
            this.loading = false;
          });

        this.displayMessage(
          "success",
          `Event for ${event.reportName} on ${eventDate} has been disabled.`
        );
      }
      this.resetMenu();
    },

    async enableEvent(event) {
      if (confirm("Are you sure you want to enable this schedule?")) {
        try {
          this.loading = true;
          const params = {
            type: "enable",
            name: event.scheduleId
          };

          await this.axios.put(
            process.env.VUE_APP_API_ADMIN_API_ENDPOINT +
              "/order-pull/schedules",
            params
          );

          // Update local state
          const schedule = this.schedules.find(
            s => s.name === event.scheduleId
          );
          if (schedule) {
            schedule.state = "ENABLED";
          }

          // Regenerate events to reflect the change
          this.generateEventsForSelectedDate();
        } catch (error) {
          console.error("Error enabling schedule:", error);
          this.displayMessage(
            "error",
            "Failed to enable schedule. Please try again."
          );
        } finally {
          this.loading = false;
        }
      }
      this.resetMenu();
    },

    async disableEvent(event) {
      if (confirm("Are you sure you want to disable this schedule?")) {
        try {
          this.loading = true;
          const params = {
            type: "disable",
            name: event.scheduleId
          };

          await this.axios.put(
            process.env.VUE_APP_API_ADMIN_API_ENDPOINT +
              "/order-pull/schedules",
            params
          );

          // Update local state
          const schedule = this.schedules.find(
            s => s.name === event.scheduleId
          );
          if (schedule) {
            schedule.state = "DISABLED";
          }

          // Regenerate events to reflect the change
          this.generateEventsForSelectedDate();
        } catch (error) {
          console.error("Error disabling schedule:", error);
          this.displayMessage(
            "error",
            "Failed to disable schedule. Please try again."
          );
        } finally {
          this.loading = false;
        }
      }
      this.resetMenu();
    },

    deleteEvent(event) {
      this.events = this.events.filter(e => e.id !== event.id);
      this.resetMenu();
    },

    showNewEventForm() {
      this.activeForm = "new";
      this.activeFormData = null;
    },

    async onFormSaved() {
      await this.refreshData();
      this.activeForm = null;
      this.activeFormData = null;
    },

    closeForm() {
      this.activeForm = null;
      this.activeFormData = null;
    },

    updateClosedItem(id, closedThisDay) {
      const selectedDate = dayjs(this.selectedDate);
      const selectedDateForClosed = selectedDate.format("MM/DD/YYYY");
      const closedItem = this.closed.find(item => item.id === id);
      if (closedThisDay) {
        if (closedItem) {
          closedItem.closed_dates.push(selectedDateForClosed);
          console.log("true closedItem:", closedItem);
        } else {
          const closedItemNew = {
            "id": id,
            "closed_dates": [
              selectedDateForClosed
            ]
          };
          this.closed.push(closedItemNew);
        }
      } else {
        if (closedItem) {
          this.removeItem(closedItem.closed_dates, selectedDateForClosed);
          console.log("false closedItem:", closedItem);
        } else {
          console.log("could not find closedItem");
        }
      }
      console.log("this.closed:", JSON.stringify(this.closed, null, 2));
    },
    removeItem(array, itemToRemove) {
      const index = array.indexOf(itemToRemove);
      if (index !== -1) {
        array.splice(index, 1);
      }
      console.log("Updated Array: ", array);
    }
  }
};
</script>

<style scoped>
.calendar-container {
  position: relative;
  font-family: Arial, sans-serif;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #ddd;
}

.date-navigation {
  display: flex;
  align-items: center;
  gap: 15px;
}

.date-picker-container {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
}

.date-picker {
  width: 150px;
  padding: 6px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
}

.date-picker:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.date-display {
  color: #495057;
  font-weight: 600;
  white-space: nowrap;
}

.header-filters {
  display: flex;
  align-items: center;
  gap: 15px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 14px;
  font-weight: 500;
  color: #495057;
  margin: 0;
  white-space: nowrap;
}

.report-type-selector {
  width: 180px;
  padding: 6px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;
}

.report-type-selector:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.calendar-body {
  display: flex;
  height: 600px;
  overflow-y: auto;
  overflow-x: auto;
}

.time-column {
  width: 80px;
  border-right: 1px solid #ddd;
  background-color: #f8f9fa;
}

.time-header {
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  border-bottom: 1px solid #ddd;
  background-color: #e9ecef;
}

.time-slot {
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.time-label {
  font-size: 12px;
  color: #666;
}

.events-column {
  flex: 1;
  position: relative;
}

.events-header {
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  border-bottom: 1px solid #ddd;
  background-color: #e9ecef;
}

.events-container {
  position: relative;
  width: 100%;
}

.time-grid-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background-color: #eee;
  z-index: 1;
}

.current-time-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background-color: #dc3545;
  z-index: 20;
  box-shadow: 0 1px 3px rgba(220, 53, 69, 0.3);
}

.current-time-indicator {
  position: absolute;
  left: -6px;
  top: -4px;
  width: 12px;
  height: 12px;
  background-color: #dc3545;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.event-item {
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
}

.event-item:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

.event-content {
  padding: 8px 12px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
}

.event-title {
  font-weight: 600;
  font-size: 13px;
  color: white;
  line-height: 1.2;
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.event-type {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.event-status {
  font-weight: 400;
  font-style: italic;
  opacity: 0.9;
}

.event-time {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.9);
  margin-top: auto;
}

.event-menu-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 14px;
  padding: 2px 4px;
  border-radius: 2px;
}

.event-menu-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Event type colors */
.event-type-ORDER_REPORT_SAME_DAY {
  background-color: #007bff;
}

.event-type-ORDER_REPORT_NEXT_DAY {
  background-color: #054b85d2;
}

.event-type-ORDER_REPORT_NEXT_DAY_PLUS_1 {
  background-color: #18096dd2;
}

.event-type-ORDER_REPORT_V_DAY {
  background-color: #06b0f3;
}

.event-type-SHR_REPORT {
  background-color: #0670a1;
}

.event-type-SHR_SAME_DAY_REPORT {
  background-color: #0670a1;
}

.event-type-SHR_NEXT_DAY_REPORT {
  background-color: #3380a3;
}

.event-type-LAKEWOOD_REPORT {
  background-color: #1eb7e6;
}

.event-type-LAKEWOOD_SAME_DAY_REPORT {
  background-color: #1eb7e6;
}

.event-type-LAKEWOOD_NEXT_DAY_REPORT {
  background-color: #44c1e7;
}

.event-type-ORDER_REPORT_MAXIMUM {
  background-color: #6525db;
}

.event-type-NOT_PULLED_REPORT {
  background-color: #e83e8c;
}

.event-type-NOT_SENT_REPORT {
  background-color: #ce35dc;
}

.event-type-ERROR_REPORT {
  background-color: #f10606;
}

.event-inactive {
  background-color: #565751 !important;
  opacity: 0.6;
}

.event-disabled {
  background-color: #7a7e81 !important;
  opacity: 0.8;
}

.event-disabled .event-title,
.event-disabled .event-type,
.event-disabled .event-status,
.event-disabled .event-time {
  color: rgba(255, 255, 255, 0.8);
}

/* Loading overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

/* Event menu */
.event-menu {
  position: fixed;
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 99999;
  font-size: 14px;
  min-width: 120px;
}

.event-menu div {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
}

.event-menu div:last-child {
  border-bottom: none;
}

.event-menu div:hover {
  background-color: #f8f9fa;
}

.text-danger {
  color: #dc3545;
}

/* Event form modal */
.event-form-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.event-form-container {
  background-color: white;
  padding: 24px;
  border-radius: 8px;
  width: 1000px;
  max-width: 90%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.form-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.form-row {
  display: flex;
  gap: 12px;
}

.form-row .col {
  flex: 1;
}

/* Edit form specific styles */
.edit-form {
  width: 1000px;
  max-width: 95%;
  max-height: 90vh;
  overflow-y: auto;
}

.form-card {
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.report-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
}

.report-label span {
  font-weight: 400;
  color: #6c757d;
  font-size: 0.9em;
}

.input-height {
  height: 38px;
}

.upper {
  text-transform: uppercase;
}

.input-box {
  min-height: 20px;
}

.input-alert {
  color: #dc3545;
  font-size: 0.875em;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.checkbox-input {
  margin-right: 8px;
  margin-top: 0;
  vertical-align: middle;
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.checkbox-label {
  margin-bottom: 0;
  font-size: 14px;
  cursor: pointer;
  user-select: none;
  line-height: 1.2;
}

.form-check {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.form-check-input {
  margin-right: 8px;
  margin-top: 0;
  vertical-align: middle;
}

.form-check-label {
  margin-bottom: 0;
  font-size: 14px;
  cursor: pointer;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.report-type-name-grid {
  display: grid !important;
  grid-template-columns: 1fr 1fr !important;
  gap: 15px !important;
}

.report-type-col,
.report-name-col {
  min-width: 0 !important;
}

@media (max-width: 575px) {
  .report-type-name-grid {
    grid-template-columns: 1fr !important;
  }
}

.delivery-time-tag-grid {
  display: grid !important;
  grid-template-columns: 1fr 1fr !important;
  gap: 15px !important;
}

.delivery-time-col,
.skipped-tag-col {
  min-width: 0 !important;
}

@media (max-width: 575px) {
  .delivery-time-tag-grid {
    grid-template-columns: 1fr !important;
  }
}

.date-delivery-grid {
  display: grid !important;
  grid-template-columns: 1fr 1fr !important;
  gap: 15px !important;
}

.date-col,
.delivery-date-col {
  min-width: 0 !important;
}

@media (max-width: 575px) {
  .date-delivery-grid {
    grid-template-columns: 1fr !important;
  }
}

.status-notes-grid {
  display: grid !important;
  grid-template-columns: 1fr 2fr !important;
  gap: 15px !important;
}

.status-col,
.notes-col {
  min-width: 0 !important;
}

@media (max-width: 575px) {
  .status-notes-grid {
    grid-template-columns: 1fr !important;
  }
}
</style>
